import logoDark from "@public/logo.svg";
import logoLight from "@public/logo_on_light.svg";
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from "@repo/shared";
import { Suspense, useState, useTransition } from "react";
import { useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "@/components/ui/link";
import { authClient } from "@/lib/auth-client";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./page.i18n";

function ResetPasswordForm() {
  const [searchParams] = useSearchParams();
  const t = useT(T);
  const [isPending, startTransition] = useTransition();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const token = searchParams.get("token");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!password || !confirmPassword) {
      setError(t.fillAllFields);
      return;
    }

    if (password.length < PASSWORD_MIN_LENGTH) {
      setError(t.passwordMinLength);
      return;
    }

    if (password.length > PASSWORD_MAX_LENGTH) {
      setError(t.passwordMaxLength);
      return;
    }

    if (password !== confirmPassword) {
      setError(t.passwordsDoNotMatch);
      return;
    }

    if (!token) {
      setError(t.invalidResetLink);
      return;
    }

    startTransition(async () => {
      const result = await authClient.resetPassword({
        newPassword: password,
        token,
      });

      if (result.error) {
        setError(result.error.message || t.failedToResetPassword);
        return;
      }

      setSuccess(true);
    });
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-full max-w-md">
          <Card className="w-full p-8">
            <div className="mb-8 text-center">
              <div className="flex justify-center mb-4">
                {/* Show light mode logo by default, dark mode logo when .dark class is present */}
                <img
                  src={logoLight}
                  alt="Quantfolio Platform"
                  width={280}
                  height={86}
                  className="dark:hidden"
                />
                <img
                  src={logoDark}
                  alt="Quantfolio Platform"
                  width={280}
                  height={86}
                  className="hidden dark:block"
                />
              </div>
              <h1 className="text-2xl font-bold">{t.invalidLink}</h1>
              <p className="text-muted-foreground mt-2">{t.linkExpiredDescription}</p>
            </div>

            <div className="space-y-4">
              <Link to="/forgot-password">
                <Button className="w-full">{t.requestNewLink}</Button>
              </Link>

              <div className="text-center">
                <Link to="/login" className="text-sm text-primary hover:underline">
                  {t.backToLogin}
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-full max-w-md">
          <Card className="w-full p-8">
            <div className="mb-8 text-center">
              <div className="flex justify-center mb-4">
                {/* Show light mode logo by default, dark mode logo when .dark class is present */}
                <img
                  src={logoLight}
                  alt="Quantfolio Platform"
                  width={280}
                  height={86}
                  className="dark:hidden"
                />
                <img
                  src={logoDark}
                  alt="Quantfolio Platform"
                  width={280}
                  height={86}
                  className="hidden dark:block"
                />
              </div>
              <h1 className="text-2xl font-bold">{t.passwordReset}</h1>
              <p className="text-muted-foreground mt-2">{t.passwordResetSuccess}</p>
            </div>

            <Link to="/login">
              <Button className="w-full">{t.signInWithNewPassword}</Button>
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-full max-w-md">
        <Card className="w-full p-8">
          <div className="mb-8 text-center">
            <div className="flex justify-center mb-4">
              {/* Show light mode logo by default, dark mode logo when .dark class is present */}
              <img
                src={logoLight}
                alt="Quantfolio Platform"
                width={280}
                height={86}
                className="dark:hidden"
              />
              <img
                src={logoDark}
                alt="Quantfolio Platform"
                width={280}
                height={86}
                className="hidden dark:block"
              />
            </div>
            <h1 className="text-2xl font-bold">{t.resetPassword}</h1>
            <p className="text-muted-foreground mt-2">{t.enterNewPassword}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">
                {t.newPassword} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoFocus
                disabled={isPending}
              />
              <p className="text-xs text-muted-foreground">{t.minCharacters}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                {t.confirmPassword} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                disabled={isPending}
              />
            </div>

            <Button type="submit" className="w-full" loading={isPending}>
              {t.resetPassword}
            </Button>

            <div className="text-center">
              <Link to="/login" className="text-sm text-primary hover:underline">
                {t.backToLogin}
              </Link>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}

function LoadingFallback() {
  const t = useT(T);
  return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-muted-foreground">{t.loading}</p>
    </div>
  );
}

export default function ResetPasswordPage() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <ResetPasswordForm />
    </Suspense>
  );
}
